import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../services/api';
import * as XLSX from 'xlsx';
import Header from '../components/Header';
import './ImportBookings.css';

const EXPECTED_COLUMNS = [
  'Afsender',
  'Modtager',
  'Afhentningsdato',
  'Afhentning kl.',
  'Leveringsdato',
  'Levering kl.',
  'Afhentning noter',
  'Levering noter',
  'Reference',
  'Antal',
  'Enhed',
];

function matchAddress(name, addresses) {
  if (!name) return null;
  const s = name.trim().toLowerCase();
  // Exact name match
  let match = addresses.find(a => a.name.toLowerCase() === s);
  if (match) return match;
  // Partial name match
  match = addresses.find(a => a.name.toLowerCase().includes(s) || s.includes(a.name.toLowerCase()));
  if (match) return match;
  // Match on address field
  match = addresses.find(a => a.address?.toLowerCase().includes(s));
  if (match) return match;
  return null;
}

function parseDate(val) {
  if (!val) return '';
  if (typeof val === 'number') {
    // Excel serial date
    const date = new Date((val - 25569) * 86400 * 1000);
    return date.toISOString().split('T')[0];
  }
  if (val instanceof Date) {
    return val.toISOString().split('T')[0];
  }
  const s = String(val).trim();
  // DD-MM-YYYY or DD/MM/YYYY
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return s;
}

function parseTime(val) {
  if (!val) return '';
  if (typeof val === 'number') {
    const totalMinutes = Math.round(val * 24 * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  const s = String(val).trim();
  const tm = s.match(/^(\d{1,2})[:\.](\d{2})/);
  if (tm) return `${tm[1].padStart(2, '0')}:${tm[2]}`;
  return s;
}

function ImportBookings() {
  const [addresses, setAddresses] = useState([]);
  const [rows, setRows] = useState([]);
  const [validationDone, setValidationDone] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    api.getAddresses().then(setAddresses).catch(console.error);
  }, []);

  const processFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const parsed = data.map((row, idx) => {
        const pickup = row['Afsender'] || row['afsender'] || row['Pickup'] || row['pickup'] || '';
        const delivery = row['Modtager'] || row['modtager'] || row['Delivery'] || row['delivery'] || '';
        const pickupMatch = matchAddress(pickup, addresses);
        const deliveryMatch = matchAddress(delivery, addresses);

        return {
          rowNum: idx + 2,
          raw: row,
          pickup_name: pickup,
          delivery_name: delivery,
          pickup_address: pickupMatch,
          delivery_address: deliveryMatch,
          pickup_date: parseDate(row['Afhentningsdato'] || row['afhentningsdato'] || row['Pickup date'] || row['pickup_date'] || ''),
          pickup_time: parseTime(row['Afhentning kl.'] || row['afhentning kl.'] || row['Pickup time'] || row['pickup_time'] || ''),
          delivery_date: parseDate(row['Leveringsdato'] || row['leveringsdato'] || row['Delivery date'] || row['delivery_date'] || ''),
          delivery_time: parseTime(row['Levering kl.'] || row['levering kl.'] || row['Delivery time'] || row['delivery_time'] || ''),
          pickup_notes: row['Afhentning noter'] || row['afhentning noter'] || row['Pickup notes'] || row['pickup_notes'] || '',
          delivery_notes: row['Levering noter'] || row['levering noter'] || row['Delivery notes'] || row['delivery_notes'] || '',
          booking_reference: row['Reference'] || row['reference'] || row['Ref'] || '',
          quantity: Number(row['Antal'] || row['antal'] || row['Quantity'] || row['quantity'] || 1),
          unit: row['Enhed'] || row['enhed'] || row['Unit'] || row['unit'] || 'Læs',
          isValid: !!pickupMatch && !!deliveryMatch,
        };
      });

      setRows(parsed);
      setValidationDone(true);
      setImportResults(null);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const validRows = rows.filter(r => r.isValid);
  const invalidRows = rows.filter(r => !r.isValid);

  const handleImport = async () => {
    setImporting(true);
    const results = { success: 0, failed: 0, errors: [] };

    for (const row of validRows) {
      try {
        await api.createBooking({
          pickup_address_id: row.pickup_address.id,
          delivery_address_id: row.delivery_address.id,
          pickup_date: row.pickup_date,
          pickup_time: row.pickup_time || '08:00',
          delivery_date: row.delivery_date || null,
          delivery_time: row.delivery_time || null,
          pickup_notes: row.pickup_notes || null,
          delivery_notes: row.delivery_notes || null,
          booking_reference: row.booking_reference || null,
          quantity: row.quantity || 1,
          unit: row.unit || 'Læs',
        });
        results.success++;
      } catch (err) {
        results.failed++;
        results.errors.push(`Række ${row.rowNum}: ${err.message}`);
      }
    }

    setImportResults(results);
    setImporting(false);
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      EXPECTED_COLUMNS,
      ['Eksempel Afsender', 'Eksempel Modtager', '25-02-2026', '08:00', '25-02-2026', '14:00', '', '', 'REF-001', 1, 'Læs'],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Bookinger');
    XLSX.writeFile(wb, 'FastTrack_Import_Skabelon.xlsx');
  };

  return (
    <div className="dashboard">
      <Header />

      <div className="import-content">
        <div className="import-header">
          <div>
            <h2 className="page-title">Import bookinger</h2>
            <p className="page-subtitle">Upload en Excel-fil for at oprette flere bookinger på én gang</p>
          </div>
          <div className="import-header-actions">
            <button className="btn-template" onClick={downloadTemplate}>
              Hent skabelon
            </button>
            <button className="btn-back" onClick={() => navigate('/')}>
              Tilbage
            </button>
          </div>
        </div>

        {!validationDone && (
          <div
            className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <div className="upload-icon">📁</div>
            <p className="upload-text">Træk en Excel-fil hertil eller klik for at vælge</p>
            <p className="upload-hint">.xlsx, .xls eller .csv</p>
          </div>
        )}

        {validationDone && !importResults && (
          <>
            <div className="validation-summary">
              <div className="summary-item success">
                <span className="summary-count">{validRows.length}</span>
                <span className="summary-label">Gyldige rækker</span>
              </div>
              <div className="summary-item error">
                <span className="summary-count">{invalidRows.length}</span>
                <span className="summary-label">Ugyldige rækker</span>
              </div>
              <div className="summary-item total">
                <span className="summary-count">{rows.length}</span>
                <span className="summary-label">Total</span>
              </div>
            </div>

            {invalidRows.length > 0 && (
              <div className="validation-errors">
                <h3>Ugyldige rækker (adresser ikke fundet)</h3>
                <table className="import-table">
                  <thead>
                    <tr>
                      <th>Række</th>
                      <th>Afsender</th>
                      <th>Modtager</th>
                      <th>Problem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invalidRows.map((row) => (
                      <tr key={row.rowNum}>
                        <td>{row.rowNum}</td>
                        <td className={row.pickup_address ? '' : 'cell-error'}>{row.pickup_name}</td>
                        <td className={row.delivery_address ? '' : 'cell-error'}>{row.delivery_name}</td>
                        <td className="cell-error">
                          {!row.pickup_address && 'Afsender ikke fundet'}
                          {!row.pickup_address && !row.delivery_address && ' + '}
                          {!row.delivery_address && 'Modtager ikke fundet'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {validRows.length > 0 && (
              <div className="valid-rows">
                <h3>Gyldige bookinger</h3>
                <table className="import-table">
                  <thead>
                    <tr>
                      <th>Række</th>
                      <th>Afsender</th>
                      <th>Modtager</th>
                      <th>Dato</th>
                      <th>Tid</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validRows.map((row) => (
                      <tr key={row.rowNum}>
                        <td>{row.rowNum}</td>
                        <td className="cell-ok">{row.pickup_address.name}</td>
                        <td className="cell-ok">{row.delivery_address.name}</td>
                        <td>{row.pickup_date}</td>
                        <td>{row.pickup_time || '08:00'}</td>
                        <td>{row.booking_reference}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="import-actions">
              <button
                className="btn-reset"
                onClick={() => { setRows([]); setValidationDone(false); }}
              >
                Vælg ny fil
              </button>
              {validRows.length > 0 && (
                <button
                  className="btn-import"
                  onClick={handleImport}
                  disabled={importing}
                >
                  {importing ? 'Importerer...' : `Opret ${validRows.length} booking${validRows.length !== 1 ? 'er' : ''}`}
                </button>
              )}
            </div>
          </>
        )}

        {importResults && (
          <div className="import-results">
            <div className={`result-box ${importResults.failed === 0 ? 'all-success' : 'partial'}`}>
              <h3>{importResults.failed === 0 ? 'Import gennemført!' : 'Import delvist gennemført'}</h3>
              <p>{importResults.success} booking{importResults.success !== 1 ? 'er' : ''} oprettet</p>
              {importResults.failed > 0 && (
                <p className="result-failed">{importResults.failed} fejlede</p>
              )}
              {importResults.errors.length > 0 && (
                <ul className="result-errors">
                  {importResults.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}
            </div>
            <div className="import-actions">
              <button className="btn-reset" onClick={() => { setRows([]); setValidationDone(false); setImportResults(null); }}>
                Importér flere
              </button>
              <button className="btn-import" onClick={() => navigate('/')}>
                Gå til dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportBookings;
